import { useState } from 'react'
import { haptic } from '@/lib/haptics'
import { createProfileWriter } from '@/onboarding/writer'
import type { Profile, Slot } from '@/profile/types'
import { useProfileStore } from '@/store/profileStore'
import { useSettingsStore } from '@/store/settingsStore'
import { voiceSession } from '@/voice/sessionController'
import { createOnboardingRunner, initialView } from './runner'
import type { OnboardingHost, OnboardingRunner, OnboardingView } from './runner'

/**
 * The first conversation, bound to this app's voice session and stores.
 *
 * Deliberately thin. Everything that decides anything is in `runner.ts`, which
 * knows nothing about React; everything that renders is in `Welcome.tsx`, which
 * decides nothing. What is left is the wiring, and the wiring is where the two
 * mistakes live: reaching for a stale closure, and rebuilding the machine on a
 * render that happens in the middle of a sentence.
 *
 * So the runner is built exactly once and every function it is handed reads its
 * dependencies at the moment it is called rather than closing over them. The
 * conversation can change language partway through, and a captured locale means
 * the second half of onboarding is spoken in the language the person has just
 * left.
 */

export interface Onboarding {
  /** Stable for the life of the screen, so callers can memoise against it. */
  runner: OnboardingRunner
  /** Changes as the conversation moves. This is the part that renders. */
  view: OnboardingView
}

export function useOnboarding(): Onboarding {
  /*
   * Seeded from the stored profile rather than from the runner, so the setter
   * exists before the runner that calls it. `initialView` is the same function
   * the runner starts from, so there is one definition of what an untouched
   * surface looks like and no second one to fall out of step with it.
   */
  const [view, setView] = useState<OnboardingView>(() =>
    initialView(useProfileStore.getState().profile),
  )

  /*
   * State with a lazy initialiser rather than a ref, and that is not a style
   * choice. The initialiser runs exactly once, which is the property wanted:
   * rebuilding the runner on a re-render would restart the conversation from
   * the step it was on, with a fresh flow and a second profile writer. A ref
   * would run once too, but a ref may not be read during render and this value
   * is rendered.
   */
  const [runner] = useState<OnboardingRunner>(() =>
    createOnboardingRunner({
      host: createHost(),
      /*
       * The device's own language, which is the answer for somebody who says
       * nothing. Read from the settings store rather than from `navigator`:
       * the store has already resolved the browser's preference list, and it
       * is also where an earlier explicit choice lives.
       */
      deviceLocale: useSettingsStore.getState().locale,
      profile: useProfileStore.getState().profile,
      onChange: setView,
    }),
  )

  return { runner, view }
}

function createHost(): OnboardingHost {
  return {
    async speak(text, options) {
      await voiceSession.speak(text, options)
    },

    listen(signal) {
      // Read per turn, because patience is adjustable mid-conversation and it
      // is the one setting somebody changes precisely because the question
      // they are on keeps cutting them off.
      return voiceSession.listen({ ...useSettingsStore.getState().timings(), signal })
    },

    cancelSpeech() {
      voiceSession.cancelSpeech()
    },

    submitTyped(text) {
      // `submitTyped`, not `answerWithChoice`. This path is only reached after
      // three failures at hearing the same answer, and that is exactly what
      // the typed-fallback count in diagnostics is for: it is the number that
      // says whether the recogniser is failing people in the field.
      voiceSession.submitTyped(text)
      haptic('turn')
    },

    /**
     * Moves the language everywhere it is read from.
     *
     * Three places, and missing any one of them shows. The settings store
     * decides which copy every screen renders; the document element decides
     * which voice a screen reader uses, through the effect in `App` that
     * watches that store; and the transport decides which recogniser and which
     * synthesised voice the conversation itself uses.
     */
    applyLocale(profile: Profile) {
      const slot = profile.language
      if (!slot) return

      if (slot.source === 'inferred') {
        /*
         * The device's own language standing in for an answer nobody gave. It
         * is not a choice and must not be recorded as one: `chooseLocale` pins
         * the language against any later change to the browser's own settings,
         * which is right for somebody who said "Hausa" out loud and wrong for
         * somebody who said nothing at all.
         */
        useSettingsStore.getState().set('locale', slot.value)
      } else {
        useSettingsStore.getState().chooseLocale(slot.value)
      }

      voiceSession.setLocale(slot.value)
    },

    writer: createProfileWriter({
      // Read fresh on every write rather than captured: "delete everything" on
      // the profile screen replaces the state this reaches into.
      local: { commit: (profile) => useProfileStore.getState().commit(merged(profile)) },
      api: voiceSession.client,
      onError: (error, slot) => {
        // Warned, not surfaced. The local copy has already landed and there is
        // nothing a person could usefully do about an HTTP status while they
        // are mid-sentence. An offline device never reaches here: the writer
        // filters that case out, because reporting the normal condition as an
        // error is how a channel stops being read.
        console.warn(`Namu: could not send "${slot}" to the server`, error)
      },
    }),
  }
}

/**
 * Folds the flow's copy of the profile onto whatever is on the device now.
 *
 * The flow holds a snapshot taken when the screen mounted, and writing that
 * snapshot back wholesale is only safe while the flow is the only thing
 * writing. It is not, once onboarding hands over: the open conversation
 * understands "call me Aminata" and writes the new name straight to the store.
 * The next volunteered fact then committed the snapshot on top of it and put
 * the old name back, silently, in the one place the person had just corrected.
 *
 * Resolved by `capturedAt` rather than by preferring either side, because
 * neither side is reliably the newer one: the flow is ahead for the answer it
 * has just taken, and the store is ahead for anything changed in conversation
 * since the screen mounted.
 */
export function merged(incoming: Profile): Profile {
  const current = useProfileStore.getState().profile

  const newer = <T,>(mine: Slot<T> | null, theirs: Slot<T> | null): Slot<T> | null => {
    if (!theirs) return mine
    if (!mine) return theirs
    return theirs.capturedAt >= mine.capturedAt ? theirs : mine
  }

  return {
    ...incoming,
    language: newer(current.language, incoming.language),
    consent: newer(current.consent, incoming.consent),
    name: newer(current.name, incoming.name),
    region: newer(current.region, incoming.region),
    ageBand: newer(current.ageBand, incoming.ageBand),
    gender: newer(current.gender, incoming.gender),
    interests: newer(current.interests, incoming.interests),
    // Never unset by a later write. Onboarding is over once it is over, and a
    // profile that forgets that gets its questions asked again from the start.
    completedAt: incoming.completedAt ?? current.completedAt,
  }
}
